import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ScrollToTop from "@/components/base/ScrollToTop";
import { categories, getCategoryBySlug } from "@/mocks/categories";
import { getProductsByCategory } from "@/mocks/products";

const PRODUCT_IMAGES: Record<string, string> = {
  "jj-hd-001": "https://readdy.ai/api/search-image?query=Heavy-duty%20industrial%20power%20connector%20HD-001%2C%20black%20and%20orange%20rugged%20design%2C%20metal%20contacts%20visible%2C%20workshop%20background%2C%20warm%20lighting%2C%20high%20contrast%20product%20photography&width=600&height=480&seq=prod-jj-hd-001&orientation=landscape",
  "jj-hd-002": "https://readdy.ai/api/search-image?query=Modular%20industrial%20rectangular%20connector%20with%20multiple%20pins%2C%20gray%20and%20black%20housing%2C%20DIN%20rail%20mounting%2C%20professional%20studio%20lighting%2C%20clean%20white%20background%2C%20technical%20product%20style&width=600&height=480&seq=prod-jj-hd-002&orientation=landscape",
  "jj-hd-003": "https://readdy.ai/api/search-image?query=Large%20circular%20industrial%20power%20connector%20with%20silver%20contacts%2C%20metal%20body%20with%20blue%20trim%2C%20industrial%20power%20distribution%20background%2C%20dramatic%20side%20lighting%2C%20professional%20product%20shot&width=600&height=480&seq=prod-jj-hd-003&orientation=landscape",
  "jj-hh-001": "https://readdy.ai/api/search-image?query=Modern%20white%20household%20power%20strip%20with%20safety%20shutters%2C%20clean%20home%20desk%20background%2C%20soft%20natural%20window%20light%2C%20minimalist%20lifestyle%20photography%2C%20warm%20cozy%20atmosphere&width=600&height=480&seq=prod-jj-hh-001&orientation=landscape",
  "jj-hh-002": "https://readdy.ai/api/search-image?query=Sleek%20white%20wall%20power%20outlet%20with%20USB-C%20ports%2C%20modern%20interior%20wall%20background%2C%20side%20angle%2C%20soft%20natural%20lighting%2C%20clean%20product%20photography%2C%20architectural%20style&width=600&height=480&seq=prod-jj-hh-002&orientation=landscape",
  "jj-hh-003": "https://readdy.ai/api/search-image?query=Smart%20WiFi%20plug%20socket%2C%20white%20minimalist%20design%20with%20LED%20indicator%2C%20modern%20smart%20home%20background%2C%20warm%20ambient%20lighting%2C%20tech%20lifestyle%20product%20photography%2C%20clean%20and%20bright&width=600&height=480&seq=prod-jj-hh-003&orientation=landscape",
  "jj-sc-001": "https://readdy.ai/api/search-image?query=Explosion-proof%20industrial%20connector%2C%20cast%20aluminum%20housing%20in%20dark%20gray%2C%20mining%20site%20background%2C%20dramatic%20directional%20lighting%2C%20rugged%20professional%20product%20photography&width=600&height=480&seq=prod-jj-sc-001&orientation=landscape",
  "jj-sc-002": "https://readdy.ai/api/search-image?query=Portable%20construction%20site%20power%20distribution%20box%2C%20yellow%20and%20black%20industrial%20design%2C%20outdoor%20construction%20site%20background%2C%20bright%20daylight%2C%20rugged%20product%20photography&width=600&height=480&seq=prod-jj-sc-002&orientation=landscape",
  "jj-pc-001": "https://readdy.ai/api/search-image?query=Miniature%20precision%20electronic%20connector%20with%20gold-plated%20pins%20on%20PCB%2C%20laboratory%20clean%20background%2C%20macro%20photography%2C%20soft%20diffused%20lighting%2C%20high-end%20technical%20aesthetic&width=600&height=480&seq=prod-jj-pc-001&orientation=landscape",
  "jj-pc-002": "https://readdy.ai/api/search-image?query=High-frequency%20coaxial%20RF%20connector%20with%20gold-plated%20SMA%20interface%2C%20technical%20laboratory%20background%20with%20test%20equipment%2C%20precise%20clean%20lighting%2C%20scientific%20product%20photography&width=600&height=480&seq=prod-jj-pc-002&orientation=landscape",
  "jj-wp-001": "https://readdy.ai/api/search-image?query=Outdoor%20waterproof%20electrical%20connector%20with%20blue%20rubber%20sealing%20ring%2C%20garden%20fountain%20background%20with%20water%20splashes%2C%20bright%20natural%20light%2C%20professional%20outdoor%20product%20photography&width=600&height=480&seq=prod-jj-wp-001&orientation=landscape",
  "jj-wp-002": "https://readdy.ai/api/search-image?query=Stainless%20steel%20underwater%20connector%20with%20dual%20O-ring%20seals%2C%20underwater%20blue%20environment%20background%20with%20bubbles%2C%20dramatic%20lighting%20through%20water%2C%20professional%20technical%20photography&width=600&height=480&seq=prod-jj-wp-002&orientation=landscape",
  "jj-wp-003": "https://readdy.ai/api/search-image?query=Quick-lock%20waterproof%20plug%20connector%2C%20blue%20and%20black%20design%2C%20outdoor%20camping%20tent%20background%2C%20warm%20sunset%20lighting%2C%20lifestyle%20adventure%20product%20photography&width=600&height=480&seq=prod-jj-wp-003&orientation=landscape",
  "jj-cs-001": "https://readdy.ai/api/search-image?query=Custom%20multi-pin%20hybrid%20connector%20assembly%20on%20engineering%20workbench%2C%20technical%20drawings%20and%203D%20models%20in%20background%2C%20warm%20desk%20lamp%2C%20professional%20industrial%20design%20photography&width=600&height=480&seq=prod-jj-cs-001&orientation=landscape",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const category = getCategoryBySlug(slug || "industrial-connectors");
  const categoryProducts = useMemo(
    () => getProductsByCategory(category.id),
    [category.id]
  );

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        {/* Category Header */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[{ label: t("nav.products") }, { label: category.name }]}
            />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mt-4 mb-3">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-600 max-w-2xl">
              {category.description}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="border-b border-background-200 bg-background-50 sticky top-16 z-30">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-3 overflow-x-auto">
            <div className="flex items-center gap-2 flex-nowrap min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    cat.id === category.id
                      ? "bg-primary-500 text-white"
                      : "bg-background-200/60 text-foreground-600 hover:bg-background-300"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Product Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <p className="text-sm text-foreground-500 mb-6">
              {t("category.all")} {categoryProducts.length}{" "}
              {t("category.products")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/category/${category.id}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer transition-all duration-300 hover:border-primary-200"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={PRODUCT_IMAGES[product.id]}
                      alt={product.name}
                      title={`${product.name} - 久久插持久插接解决方案`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-xs text-foreground-600 line-clamp-2 mb-4 leading-relaxed">
                      {product.description}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {product.features.map((feat, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700"
                        >
                          {feat}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-base font-bold text-primary-600">
                        {product.price}
                      </span>
                      <span className="text-xs text-foreground-500 flex items-center gap-1">
                        {t("products.viewDetail")}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Category Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: `${category.name} - 久久插`,
              description: category.description,
              url: `https://www.jiujiucha.com/category/${category.slug}`,
              isPartOf: {
                "@type": "WebSite",
                name: "久久插",
                url: "https://www.jiujiucha.com/",
              },
              breadcrumb: {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "首页",
                    item: "https://www.jiujiucha.com/",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "产品中心",
                    item: "https://www.jiujiucha.com/category/industrial-connectors",
                  },
                  {
                    "@type": "ListItem",
                    position: 3,
                    name: category.name,
                  },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}